import { handleDefiSdkError, handleGenericSdkError, handleStakewiseSdkError } from "./error";
/**
 * Core operation executor with consistent logging and error handling.
 *
 * On failure the `errorHandler` is invoked with the caught error and the
 * logger.  If the handler **returns** an error object it is thrown by the
 * executor; if it **throws** directly (e.g. async handlers) the rejection
 * propagates as-is.
 *
 * Prefer the pre-configured executors produced by {@link createExecutor}.
 */
async function executeOperation(operationName, logger, operationPromise, errorHandler) {
    logger.debug(`Executing ${operationName}`);
    try {
        const result = await operationPromise;
        logger.debug(`${operationName} completed successfully`);
        return result;
    }
    catch (error) {
        logger.error(`${operationName} failed`, { error });
        throw await errorHandler(error, logger);
    }
}
/**
 * Creates a typed operation executor pre-loaded with a specific error handler.
 *
 * ```ts
 * const exec = createExecutor(handleStakewiseSdkError);
 * return exec("getVault", this.logger, _getVault(sdk, address));
 * ```
 */
function createExecutor(errorHandler) {
    return (operationName, logger, operationPromise) => executeOperation(operationName, logger, operationPromise, errorHandler);
}
/** Pre-configured executor for StakeWise protocol operations. */
export const executeStakewiseOperation = createExecutor(handleStakewiseSdkError);
/** Pre-configured executor for generic SDK operations (common, solana). */
export const executeGenericOperation = createExecutor(handleGenericSdkError);
/** Pre-configured executor for DeFi module operations. */
export const executeDefiOperation = createExecutor(handleDefiSdkError);
//# sourceMappingURL=data:application/json;base64,eyJ2ZXJzaW9uIjozLCJmaWxlIjoiZXhlY3V0b3IuanMiLCJzb3VyY2VSb290IjoiIiwic291cmNlcyI6WyIuLi8uLi8uLi8uLi9zcmMvbWFpbi9zaGFyZWQvZXhlY3V0b3IudHMiXSwibmFtZXMiOltdLCJtYXBwaW5ncyI6IkFBQUEsT0FBTyxFQUFFLGtCQUFrQixFQUFFLHFCQUFxQixFQUFFLHVCQUF1QixFQUFFLE1BQU0sU0FBUyxDQUFDO0FBRzdGOzs7Ozs7Ozs7R0FTRztBQUNILEtBQUssVUFBVSxnQkFBZ0IsQ0FDN0IsYUFBcUIsRUFDckIsTUFBYyxFQUNkLGdCQUE0QixFQUM1QixZQUF5RDtJQUV6RCxNQUFNLENBQUMsS0FBSyxDQUFDLGFBQWEsYUFBYSxFQUFFLENBQUMsQ0FBQztJQUMzQyxJQUFJLENBQUM7UUFDSCxNQUFNLE1BQU0sR0FBRyxNQUFNLGdCQUFnQixDQUFDO1FBQ3RDLE1BQU0sQ0FBQyxLQUFLLENBQUMsR0FBRyxhQUFhLHlCQUF5QixDQUFDLENBQUM7UUFDeEQsT0FBTyxNQUFNLENBQUM7SUFDaEIsQ0FBQztJQUFDLE9BQU8sS0FBSyxFQUFFLENBQUM7UUFDZixNQUFNLENBQUMsS0FBSyxDQUFDLEdBQUcsYUFBYSxTQUFTLEVBQUUsRUFBRSxLQUFLLEVBQUUsQ0FBQyxDQUFDO1FBQ25ELE1BQU0sTUFBTSxZQUFZLENBQUMsS0FBSyxFQUFFLE1BQU0sQ0FBQyxDQUFDO0lBQzFDLENBQUM7QUFDSCxDQUFDO0FBRUQ7Ozs7Ozs7R0FPRztBQUNILFNBQVMsY0FBYyxDQUFDLFlBQXlEO0lBQy9FLE9BQU8sQ0FBSSxhQUFxQixFQUFFLE1BQWMsRUFBRSxnQkFBNEIsRUFBYyxFQUFFLENBQzVGLGdCQUFnQixDQUFDLGFBQWEsRUFBRSxNQUFNLEVBQUUsZ0JBQWdCLEVBQUUsWUFBWSxDQUFDLENBQUM7QUFDNUUsQ0FBQztBQUVELGlFQUFpRTtBQUNqRSxNQUFNLENBQUMsTUFBTSx5QkFBeUIsR0FBRyxjQUFjLENBQUMsdUJBQXVCLENBQUMsQ0FBQztBQUVqRiwyRUFBMkU7QUFDM0UsTUFBTSxDQUFDLE1BQU0sdUJBQXVCLEdBQUcsY0FBYyxDQUFDLHFCQUFxQixDQUFDLENBQUM7QUFFN0UsMERBQTBEO0FBQzFELE1BQU0sQ0FBQyxNQUFNLG9CQUFvQixHQUFHLGNBQWMsQ0FBQyxrQkFBa0IsQ0FBQyxDQUFDIn0=